/**
 * The Custom Variables outliner's model.
 *
 * A snapshot's `globalVariables` is already a hierarchy, so the panel shows it
 * the way Version Settings shows components: CONTAINERS (objects and arrays) as
 * rows in a tree, and the selected container's PRIMITIVES as fields below.
 *
 * That split is not cosmetic. Rendering the variables inline mounts one input
 * per leaf — ~1,900 of them on a real snapshot — and every keystroke re-renders
 * all of them. Splitting it puts at most a few dozen on screen at once.
 *
 * Everything here is derived from the variables on the way to the screen.
 * Nothing is stored, and the snapshot gains no new structure.
 */

import _ from "lodash";
import { matchesTabSearch } from "./playEngineTabSearch";

/**
 * The tree's single root: the `globalVariables` object itself.
 *
 * Not a synthetic group — it is the real object every other row hangs off, and
 * the variables declared directly on it are its own fields. Naming that row
 * after anything else (the game, say) makes it read as a config group the
 * snapshot declares, which is exactly the confusion this id avoids.
 */
export const ROOT_NODE_ID = "__root";

/**
 * Keys worth showing instead of a bare index when labelling an array item.
 * First match wins, so the order is "most name-like first".
 */
const ARRAY_ITEM_LABEL_KEYS = ["title", "name", "label", "question", "text", "caption", "id", "key"];

/** "someFieldName" → "Some Field Name" */
export function labelFromKey(key) {
  return String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/** Convert a path array to dotted string: ["rounds", 0, "question"] → "rounds.0.question" */
export function pathToDotted(path) {
  return path.join(".");
}

/**
 * Returns true if the given dotted path is hidden.
 * Supports exact matches and wildcard segments using "*".
 * e.g. hidden=["questions.*.correct"] hides "questions.0.correct", "questions.1.correct", etc.
 */
export function isHiddenPath(dotted, hiddenSet) {
  if (!hiddenSet || hiddenSet.size === 0) return false;
  if (hiddenSet.has(dotted)) return true;
  for (const pattern of hiddenSet) {
    if (!pattern.includes("*")) continue;
    // "*" is escaped along with everything else, THEN turned into a segment
    // matcher. Leaving it out of the character class was the long-standing bug
    // here: an unescaped "*" made the preceding "\." a quantifier, so
    // "q.*.correct" compiled to /^q\.*\.correct$/ and matched "q.correct"
    // while missing every "q.<n>.correct" it was written for.
    const regex = new RegExp(
      "^" + pattern.replace(/[.+?^${}()|[\]\\*]/g, "\\$&").replace(/\\\*/g, "[^.]+") + "$"
    );
    if (regex.test(dotted)) return true;
  }
  return false;
}

function truncateLabel(text, max = 28) {
  const flat = String(text).replace(/\s+/g, " ").trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}

/**
 * "#3  Garden Steps" rather than "3".
 *
 * Arrays of objects are the common shape here (rounds, questions, slides) and an
 * outliner of bare indices is unreadable. The label is display-only — the node is
 * still addressed by its index in the dotted path.
 */
export function arrayItemLabel(item, index) {
  if (item !== null && typeof item === "object" && !Array.isArray(item)) {
    for (const key of ARRAY_ITEM_LABEL_KEYS) {
      const value = item[key];
      if (typeof value === "string" && value.trim()) return `#${index}  ${truncateLabel(value)}`;
      if (typeof value === "number") return `#${index}  ${value}`;
    }
  }
  return `#${index}`;
}

function isContainer(value) {
  return value !== null && typeof value === "object";
}

function entriesOf(value) {
  return Array.isArray(value) ? value.map((item, index) => [index, item]) : Object.entries(value);
}

/**
 * How many values a container may hold and still count as one compound VALUE
 * rather than a group of its own.
 *
 * Sized to the shapes the playables actually use — `{portrait, landscape}`,
 * `{x, y}`, `{color, alpha}` (2 each) and `{portrait:{x,y}, landscape:{x,y}}`,
 * `{r,g,b,a}` (4 each) — without naming any of them. A game that invents
 * `{min, max}` tomorrow is covered for free.
 */
export const MAX_VALUE_LEAVES = 4;

/**
 * How many values an ARRAY may hold and still be shown whole, with its items
 * folded into its own panel rather than each taking a row.
 *
 * A list is usually read and edited as a list — translating twelve rounds by
 * clicking twelve rows is the wrong shape of work — so an array that fits is
 * laid out in one place. The cap is what stops that from bringing back the
 * problem the outliner exists to solve: without it, real snapshots produce
 * panels of 300–900 fields.
 */
export const MAX_ARRAY_INLINE_LEAVES = 60;

/** Total number of primitives under a value, however deeply nested. */
function countLeaves(value) {
  if (!isContainer(value)) return 1;
  let total = 0;
  for (const [, child] of entriesOf(value)) total += countLeaves(child);
  return total;
}

/**
 * True when a container is a compound VALUE — something to edit in its parent's
 * panel — rather than a GROUP that earns a row of its own.
 *
 * Three conditions, none of which look at a key name:
 *   1. not an array — a list stays a list, however short
 *   2. at most MAX_VALUE_LEAVES values in the whole subtree
 *   3. every child container is itself a value, so a value never hides a group
 *
 * Note this reads the RAW data: hiding fields does not turn a group into a
 * value, which keeps the tree's shape stable across inspector configs.
 */
export function isValueNode(value, maxLeaves = MAX_VALUE_LEAVES) {
  if (!isContainer(value) || Array.isArray(value)) return false;
  if (countLeaves(value) > maxLeaves) return false;
  return entriesOf(value).every(([, child]) => !isContainer(child) || isValueNode(child, maxLeaves));
}

/**
 * Flatten one compound value into the editable leaves its parent's panel shows.
 *
 * Labels are the path WITHIN the value ("Portrait · X"), so a field still says
 * what it is once it has left its own row behind.
 */
function collectValueLeaves(value, path, labelParts, hiddenKeys, out) {
  for (const [key, child] of entriesOf(value)) {
    const childPath = [...path, key];
    const dotted = pathToDotted(childPath);
    if (isHiddenPath(dotted, hiddenKeys)) continue;

    const label = typeof key === "number" ? `#${key}` : labelFromKey(key);
    if (isContainer(child)) {
      collectValueLeaves(child, childPath, [...labelParts, label], hiddenKeys, out);
    } else {
      out.push({ key, dotted, path: childPath, label: [...labelParts, label].join(" · ") });
    }
  }
}

/**
 * Build the outliner: one node per container, carrying the primitive keys that
 * belong to it.
 *
 * Hidden paths (`inspectorConfig.globalVariables.hidden`) are dropped here
 * rather than at render time, so a container whose every child is hidden never
 * reaches the tree at all.
 *
 * Returns a single root node — the `globalVariables` object — whose children are
 * the containers it holds and whose fields are the variables declared directly
 * on it. An empty tree comes back as `[]` so the caller can show its own empty
 * state rather than a root with nothing under it.
 *
 * A container that holds only a handful of values is folded into its parent's
 * panel instead of taking a row (see `isValueNode`), so `{portrait, landscape}`
 * is edited where it is used rather than one click further down.
 *
 * @param {object} variables   the English base tree
 * @param {Set<string>} hiddenKeys
 * @param {string} rootLabel   what to call the root row
 * @param {number} maxValueLeaves  the value/group threshold; see MAX_VALUE_LEAVES
 * @param {number} maxArrayInlineLeaves  when a whole array is shown in one
 *   panel; see MAX_ARRAY_INLINE_LEAVES
 */
export function buildVariableTree(
  variables,
  hiddenKeys,
  rootLabel = "Variables",
  maxValueLeaves = MAX_VALUE_LEAVES,
  maxArrayInlineLeaves = MAX_ARRAY_INLINE_LEAVES
) {
  if (!isContainer(variables)) return [];

  /**
   * Split one container's entries into the three things the UI needs:
   *   fields         — what its panel renders, in order (leaves and values)
   *   primitiveKeys  — the same leaves, flattened; what "has fields" and the
   *                    row's count badge read
   *   children       — the groups that get rows of their own
   *
   * `shouldFold` decides, per child, whether it is folded in or gets a row.
   * Null means nothing folds — which is the root's rule, since its children are
   * the playable's parts and that list is the panel's primary navigation.
   */
  function collectEntries(value, path, shouldFold, labelFor) {
    const fields = [];
    const primitiveKeys = [];
    const children = [];

    for (const [key, child] of entriesOf(value)) {
      const childPath = [...path, key];
      const dotted = pathToDotted(childPath);
      if (isHiddenPath(dotted, hiddenKeys)) continue;

      if (!isContainer(child)) {
        const leaf = { key, dotted, path: childPath };
        fields.push({ kind: "leaf", ...leaf });
        primitiveKeys.push(leaf);
        continue;
      }

      if (shouldFold && shouldFold(child)) {
        const leaves = [];
        collectValueLeaves(child, childPath, [], hiddenKeys, leaves);
        // Everything inside it was hidden — the value has nothing left to show.
        if (leaves.length === 0) continue;
        fields.push({
          kind: "value",
          key,
          dotted,
          label: labelFor(key, child),
          leaves,
        });
        for (const leaf of leaves) {
          primitiveKeys.push({ key: leaf.key, dotted: leaf.dotted, path: leaf.path });
        }
        continue;
      }

      const node = walk(child, childPath);
      // A container with nothing left to show after hiding is not a row.
      if (node.primitiveKeys.length > 0 || node.children.length > 0) children.push(node);
    }

    return { fields, primitiveKeys, children };
  }

  function walk(value, path) {
    let shouldFold;
    let labelFor;
    if (Array.isArray(value)) {
      // A short-enough list is shown whole, items and all; a long one keeps its
      // items as rows so no single panel has to carry the entire list.
      const whole = countLeaves(value) <= maxArrayInlineLeaves;
      shouldFold = whole ? () => true : null;
      labelFor = (key, child) => arrayItemLabel(child, key);
    } else {
      shouldFold = (child) => isValueNode(child, maxValueLeaves);
      labelFor = (key) => (typeof key === "number" ? `#${key}` : labelFromKey(key));
    }
    const { fields, primitiveKeys, children } = collectEntries(value, path, shouldFold, labelFor);
    const key = path[path.length - 1];
    return {
      id: pathToDotted(path),
      label: typeof key === "number" ? String(key) : labelFromKey(key),
      kind: Array.isArray(value) ? "array" : "object",
      count: Array.isArray(value) ? value.length : null,
      fields,
      primitiveKeys,
      children,
    };
  }

  const root = collectEntries(variables, [], null, (key) => labelFromKey(key));
  if (root.primitiveKeys.length === 0 && root.children.length === 0) return [];

  return [{
    id: ROOT_NODE_ID,
    label: rootLabel,
    kind: "root",
    count: null,
    fields: root.fields,
    primitiveKeys: root.primitiveKeys,
    children: root.children,
  }];
}

/**
 * Re-label array items from their own contents, which `walk` cannot see from
 * inside the item. Mutates and returns the tree it is given.
 */
export function labelArrayChildren(nodes, variables) {
  for (const node of nodes) {
    if (node.kind === "array") {
      for (const child of node.children) {
        const index = Number(child.id.slice(node.id.length + 1));
        if (Number.isInteger(index)) {
          child.label = arrayItemLabel(_.get(variables, child.id.split(".")), index);
        }
      }
    }
    labelArrayChildren(node.children, variables);
  }
  return nodes;
}

/** Flatten to `id -> node` so selection and expansion resolve in one hop. */
export function indexTree(nodes, into = new Map()) {
  for (const node of nodes) {
    into.set(node.id, node);
    indexTree(node.children, into);
  }
  return into;
}

/** Keep only variable knobs matching a query, with their ancestor containers. */
export function filterVariableTree(nodes, query) {
  if (!String(query || "").trim()) return nodes;
  const matches = (value) => matchesTabSearch(value, query);

  function filterNode(node) {
    if (matches(node.label) || matches(node.id)) return node;
    const fields = node.fields.reduce((kept, field) => {
      if (field.kind === "leaf") {
        if (matches(field.key) || matches(labelFromKey(field.key))) kept.push(field);
      } else {
        const leaves = field.leaves.filter((leaf) =>
          matches(leaf.key) || matches(leaf.label) || matches(`${field.label} ${leaf.label}`)
        );
        if (matches(field.key) || matches(field.label)) kept.push(field);
        else if (leaves.length) kept.push({ ...field, leaves });
      }
      return kept;
    }, []);
    const children = node.children.map(filterNode).filter(Boolean);
    return fields.length || children.length ? { ...node, fields, children } : null;
  }

  return nodes.map(filterNode).filter(Boolean);
}

/**
 * Every ancestor id of a node, outermost first — the rows that must be open for
 * it to be visible. The root comes first for everything but itself, since every
 * other node now hangs off it.
 */
export function ancestorIds(nodeId) {
  if (!nodeId || nodeId === ROOT_NODE_ID) return [];
  const parts = nodeId.split(".");
  const out = [ROOT_NODE_ID];
  for (let i = 1; i < parts.length; i++) out.push(parts.slice(0, i).join("."));
  return out;
}
